import {
  getArtifactHtmlDocumentTemplateId,
  htmlDocumentTemplateHasBody,
  type HtmlDocumentArtifactRef,
  type HtmlDocumentTemplateBodyRef
} from './html-document-artifact';

type DesignedDocumentRef = HtmlDocumentArtifactRef & {
  printsLegacyLook?: boolean;
  document?: {
    htmlDocumentTemplateId?: string | null;
    resolvedDesign?: {
      designId?: string | null;
      name: string;
      archived: boolean;
      hasPages: boolean;
    } | null;
  } | null;
};

export type DocumentDesign = {
  /** Linked to a Library Style, whose look the document keeps: it never takes a design. */
  styleLinked: boolean;
  /** Linked to a template with a body, whose page choices are frozen when marked final. */
  templateLinked: boolean;
  /**
   * Prints a Legacy template's own look with no design, as the server decides from its history
   * (useDocumentPrintsLegacyLook).
   */
  templateOwnLook: boolean;
  /** The design the version renders with. Both are null when it renders with none. */
  designId: string | null;
  designName: string | null;
  archived: boolean;
};

type TemplateRef = HtmlDocumentTemplateBodyRef & { id: string };

/** Whether the document is linked to a template with a body, rather than to a Style. */
export const linksTemplateWithBody = (
  artifact: HtmlDocumentArtifactRef,
  templates: TemplateRef[]
): boolean => {
  const templateId = getArtifactHtmlDocumentTemplateId(artifact);
  const template = templateId ? templates.find((each) => each.id === templateId) : undefined;
  return !!template && htmlDocumentTemplateHasBody(template);
};

/** The design a document version renders with, as the server resolves it for the renderer. */
export const documentDesign = (
  artifact: DesignedDocumentRef,
  templates: TemplateRef[]
): DocumentDesign => {
  const templateId = getArtifactHtmlDocumentTemplateId(artifact);
  const hasBody = linksTemplateWithBody(artifact, templates);
  const resolved = artifact.document?.resolvedDesign;
  return {
    // Built-in Styles have no template row, and a template this caller cannot see may be a
    // Style too, so anything but a template known to have a body counts as one.
    styleLinked: !!templateId && (templateId.startsWith('builtin:') || !hasBody),
    templateLinked: hasBody,
    templateOwnLook: hasBody && !!artifact.printsLegacyLook,
    designId: resolved?.designId ?? null,
    designName: resolved?.name ?? null,
    archived: !!resolved?.archived
  };
};

/** A saved template's design that cannot be told until the agency's designs load. */
export const DESIGN_CHOICES_LOADING = Symbol('design choices loading');

/**
 * The design a template saved from a document version keeps, as saveTemplate takes it. A version
 * on a design keeps it, and one with no design saves null, a None template, which prints the body
 * as written. That includes a version printing a Legacy template's own look, since a save cannot
 * make a Legacy template. Undefined saves the template on the agency default: for a Style-linked
 * version with no design, whose look is its Style's; while the Design menu offers no choice, since
 * nobody chose None from a menu that is hidden and a design the agency adds later should reach the
 * template, as convert-styles has plain templates take it; and for a design that is not one of the
 * choices, since the save fails for a design the caller cannot read or one that is archived. While
 * the designs load, it is DESIGN_CHOICES_LOADING, for the save to wait on.
 */
export const savedTemplateDesignId = (
  design: DocumentDesign,
  rendersWithDesign: boolean,
  { choices, loading }: { choices: { designs: { id: string }[] } | null; loading: boolean }
): string | null | undefined | typeof DESIGN_CHOICES_LOADING => {
  if (loading) return DESIGN_CHOICES_LOADING;
  if (!choices?.designs.length) return undefined;
  if (!rendersWithDesign) return design.styleLinked ? undefined : null;
  return choices.designs.some((each) => each.id === design.designId)
    ? (design.designId ?? undefined)
    : undefined;
};
